// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isMultilineAuditValue } from '@/audit/utils/is-multiline-audit-value';
import { Typography, TypographyProps } from '@mui/material';
import { DateTime } from 'luxon';
import { ForwardedRef, forwardRef } from 'react';

/**
 * Gets a display label for a value
 */
function getLabel(value: string | number | boolean | DateTime) {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return value.toString();
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (value instanceof DateTime) {
    return value.toLocaleString({ dateStyle: 'medium' });
  }
  return String(value);
}

interface Props<Value> extends TypographyProps {
  /**
   * A property value to render.
   */
  value: Value | null;

  /**
   * If provided, a function that returns a label for the given value.
   */
  getLabelForValue?: (value: Value) => string;
}

/**
 * A function that renders a single property value for an audit entry and
 * forwards a ref to the underlying <span> element. For compatibility with
 * Material UI, all incoming props are spread onto the <span> element.
 */
function AuditPropertyValueRenderer<
  Value extends string | number | boolean | DateTime,
>(props: Props<Value>, ref: ForwardedRef<HTMLSpanElement>) {
  // Unfortunately, Material UI requires that the component props are spread
  // onto the element when forwarding refs. This is done so that a tooltip (or
  // some other Material UI component) can be attached to the element. Simply
  // forwarding the ref will not work. In both branches, the ref is forwarded
  // to the Typography component and all incomining props are spread onto it.

  // Destructure getLabelForValue and value from props, and keep the rest
  const {
    getLabelForValue,
    value,
    variant = 'inherit',
    sx,
    ...restProps
  } = props;

  if (value === null) {
    return (
      <Typography
        {...restProps}
        ref={ref}
        variant={variant}
        component="span"
        color="text.secondary"
      >
        None
      </Typography>
    );
  }

  const label = getLabelForValue?.(value) ?? getLabel(value);

  const isMultilineString = isMultilineAuditValue(value);
  // Anything with a space in it is prose — an error message, a note, a name — and
  // has a natural break point, so let it wrap to as many lines as it needs. A
  // character count cannot decide this: the available width is whatever the caller
  // gives it, and a 78-character sync error clipped mid-sentence in a drawer.
  // Single-token values (keys, SKUs, numbers, references) keep the single line and
  // ellipsis, since breaking mid-token reads worse than truncating.
  const hasWordBreak = typeof value === 'string' && /\s/.test(value);
  const isLongText = typeof value === 'string' && value.length > 100;
  const shouldWrapText = isMultilineString || hasWordBreak || isLongText;

  return (
    <Typography
      {...restProps}
      ref={ref}
      variant={variant}
      component="span"
      sx={[
        shouldWrapText
          ? {
              whiteSpace: isMultilineString ? 'pre-wrap' : 'normal',
              wordBreak: 'break-word',
              maxWidth: '100%',
              display: 'block',
            }
          : {
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              maxWidth: '100%',
            },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {label}
    </Typography>
  );
}

/**
 * A component that renders a single property value for an audit entry. This
 * component forwards a ref to the underlying <span> element.
 */
export const AuditPropertyValue = forwardRef(AuditPropertyValueRenderer);
